// exclusionBase — 「人を外して戻す」で、確定時にサーバが土台にする世代 G を web で決める
// (exclusion-ui-investigation-2026-10-10.md「③-a: web が G を決める」)。
//
// 規則はサーバと同じコードを使う (`@oekaki/protocol` の `resolveLineage` /
// `planExclusionDecisions`)。ここは**入力の組み立て**だけを持つ純関数で、サーバ
// (`ExclusionBakeService` 手順 2〜4) と同じ入力を同じ規則で作る:
//   - 系譜: 絵ログの境界 (全消し / restore マーカー) + 先頭を `resolveLineage` に渡す
//   - leaf = 現行セッション (現行文書) / 行のセッション (過去キャンバス行)
//   - baked = `kind: 'excluded'` の過去キャンバス行のセッション
//   - 世代 = 系譜の各セッションの世代一覧 (seed コピー込みの全部。サーバも畳まない)
// 世代一覧・系譜が取れなかったときは null (= G を出さず従来どおり。サーバが正)。

import {
  type ExclusionDecisions,
  type ExclusionDecisionsResult,
  isDocumentBoundary,
  type LineageDoc,
  normalizeEndSeq,
  type PlanEvent,
  planExclusionDecisions,
  resolveLineage,
  type StrokeLogEvent,
  toPlanEvent,
} from '@oekaki/protocol';

export interface ExclusionLineageInput {
  /** 読み込んだ絵ログ (系譜の根まで、seq 昇順)。 */
  readonly events: readonly StrokeLogEvent[];
  /** 過去キャンバス行の終端 (`max(clearedSeq, snapshotSeq)`)。現行文書は省略。 */
  readonly rowEndSeq?: number;
  readonly leafSessionId: string;
  /** `kind: 'excluded'` の過去キャンバス行のセッション。 */
  readonly bakedSessionIds: ReadonlySet<string>;
}

export interface WindowedLineageInput extends ExclusionLineageInput {
  /** 読み込んでいない古い絵ログがまだ残っているか (窓が途中から始まっている)。 */
  readonly hasOlder: boolean;
  /** 読み込んだ最も古いセグメントの fromSeq。仮の根の土台候補の下限に使う。 */
  readonly floorSeq: number | null;
}

export interface WindowedLineage {
  readonly docs: readonly LineageDoc[];
  /**
   * true = 系譜の根まで読めていない (窓の下端が仮の根)。仮の根は origin `'restore'` 扱いで、
   * 「白紙から焼く」とは判断しない (土台の世代が窓内に見つからなければ no_base_generation になり、
   * 呼び出し側が窓を広げる)。土台に使える世代は、その後ろのイベントが窓に全部入っている
   * `floorSeq - 1` 以降。
   */
  readonly truncated: boolean;
}

/**
 * 読み込んだ窓から系譜 (根 → leaf) を作る。窓が根まで届いていなければ、窓の下端を仮の根に
 * した系譜を返す (`truncated: true`)。解決できなければ null。
 */
export const resolveWindowedLineage = (input: WindowedLineageInput): WindowedLineage | null => {
  const last = input.events[input.events.length - 1];
  if (last === undefined) return null;
  const endSeq =
    input.rowEndSeq === undefined ? last.seq : normalizeEndSeq(input.events, input.rowEndSeq);
  // サーバと同じく、系譜が読むのは境界イベント + 先頭だけ。
  const head = input.events[0] as StrokeLogEvent;
  const lineageEvents = [head, ...input.events.filter((e) => e !== head && isDocumentBoundary(e))];
  const resolve = (hasEarlierSegments: boolean) =>
    resolveLineage({
      events: lineageEvents,
      endSeq,
      leafSessionId: input.leafSessionId,
      bakedSessionIds: input.bakedSessionIds,
      hasEarlierSegments,
    });
  const full = resolve(input.hasOlder);
  if (full.kind === 'ok') return { docs: full.docs, truncated: false };
  // 窓の下端までで系譜が尽きた: 下端を仮の根にして解き直す。
  const truncatedRes = resolve(false);
  if (truncatedRes.kind !== 'ok') return null;
  const [root, ...rest] = truncatedRes.docs;
  if (root === undefined) return null;
  const floor = input.floorSeq ?? root.eventsFrom;
  return {
    docs: [{ ...root, origin: 'restore', candidateMin: Math.max(0, floor - 1) }, ...rest],
    truncated: true,
  };
};

/** 系譜 (根 → leaf)。解決できなければ null。窓の下端は根とみなす (読み込みは根まで降りている前提)。 */
export const resolveExclusionDocs = (input: ExclusionLineageInput): readonly LineageDoc[] | null =>
  resolveWindowedLineage({ ...input, hasOlder: false, floorSeq: null })?.docs ?? null;

/** 系譜が使うセッション (重複なし)。世代一覧を取る対象。 */
export const lineageSessionIds = (docs: readonly LineageDoc[]): readonly string[] => [
  ...new Set(docs.map((d) => d.sessionId)),
];

export const toPlanEvents = (events: readonly StrokeLogEvent[]): readonly PlanEvent[] =>
  events.map((e) => toPlanEvent(e, (s) => s));

export interface BaseGenerationInfo {
  readonly sessionId: string;
  readonly seq: number;
  /** ISO8601 (世代の保存時刻)。一覧に無ければ null。 */
  readonly savedAt: string | null;
}

export type ExclusionBaseVerdict =
  /** 世代 G から作り直す。 */
  | { readonly kind: 'base'; readonly base: BaseGenerationInfo }
  /** 白紙から焼く (系譜の根が全消し / 先頭)。 */
  | { readonly kind: 'blank' }
  | { readonly kind: 'nothing_to_exclude' }
  | { readonly kind: 'no_base_generation' };

export interface PlanBaseInput {
  readonly planEvents: readonly PlanEvent[];
  readonly docs: readonly LineageDoc[];
  readonly excludeUids: readonly string[];
  readonly excludeSids: readonly string[];
  /** セッション → 世代 (seq / 保存時刻)。 */
  readonly generations: ReadonlyMap<
    string,
    ReadonlyArray<{ readonly seq: number; readonly savedAt: string }>
  >;
}

export interface ExclusionBasePlan {
  readonly verdict: ExclusionBaseVerdict;
  /** 土台 (G) とイベントごとの扱い。verdict が error のときは null。 */
  readonly decisions: ExclusionDecisions | null;
}

export const planExclusion = (input: PlanBaseInput): ExclusionBasePlan => {
  const generationsBySession = new Map<string, readonly number[]>();
  for (const [sid, gens] of input.generations) {
    generationsBySession.set(
      sid,
      gens.map((g) => g.seq),
    );
  }
  const res: ExclusionDecisionsResult = planExclusionDecisions({
    events: input.planEvents,
    docs: input.docs,
    excludeUids: new Set(input.excludeUids),
    excludeSids: new Set(input.excludeSids),
    generationsBySession,
  });
  if (res.kind === 'error') return { verdict: { kind: res.code }, decisions: null };
  const base = res.decisions.base;
  if (base === null) return { verdict: { kind: 'blank' }, decisions: res.decisions };
  const savedAt =
    input.generations.get(base.sessionId)?.find((g) => g.seq === base.seq)?.savedAt ?? null;
  return {
    verdict: { kind: 'base', base: { sessionId: base.sessionId, seq: base.seq, savedAt } },
    decisions: res.decisions,
  };
};

export const planExclusionBase = (input: PlanBaseInput): ExclusionBaseVerdict =>
  planExclusion(input).verdict;
